import { OrthographicCamera } from 'three';

import { newVector3, SceneObjectBounds } from '@paragon/three-shared';

import {
  convertFromDesignToLayoutCoordinates,
  convertFromLayoutToDesignCoordinates,
} from '../library/layout-geometry/coordinates';
import { CameraType } from '../store/slices/camera/types';
import { ANTI_ZOOM_FACTOR_PER_VIEWPORT_HEIGHT, updateAntiZoomFactor } from './antiZoom';
import { DesignClientCameraMethods } from './DesignClientCamera';
import { getLayoutModeCameraControls } from './reactThreeFiberBindings';

// Since the camera height is hard-coded for now, this number MUST be greater than the height of the tallest object in
//   the scene (to prevent clipping) and less than the camera's far frustum (2000).
const HEIGHT = 1900;

const ZOOM_FACTOR = 0.75;

export class OrthographicLayoutCamera extends OrthographicCamera implements DesignClientCameraMethods {
  public type: CameraType = 'orthographic-layout';

  viewportWidth: number;
  viewportHeight: number;

  constructor(width: number, height: number) {
    super(width / -2, width / 2, height / 2, height / -2);

    this.viewportWidth = width;
    this.viewportHeight = height;

    this.position.setY(HEIGHT);

    this.updateProjectionMatrix();
  }

  updateViewportSize(width: number, height: number): void {
    this.viewportWidth = width;
    this.viewportHeight = height;

    this.left = width / -2;
    this.right = width / 2;
    this.top = height / 2;
    this.bottom = height / -2;

    this.updateProjectionMatrix();
  }

  getCurrentBounds(): SceneObjectBounds {
    const width = (this.viewportWidth * ZOOM_FACTOR) / this.zoom;
    const height = (this.viewportHeight * ZOOM_FACTOR) / this.zoom;

    const position = convertFromDesignToLayoutCoordinates(this.position);

    return {
      position: { x: position.x, y: position.y },
      size: { width, height },
    };
  }

  focusTo(bounds: SceneObjectBounds) {
    this.zoom =
      Math.min(this.viewportWidth / bounds.size.width, this.viewportHeight / bounds.size.height) * ZOOM_FACTOR;

    const desiredPosition = { ...bounds.position, z: HEIGHT };

    this.position.copy(newVector3(convertFromLayoutToDesignCoordinates(desiredPosition)));
    this.updateProjectionMatrix();

    const { x, y, z } = convertFromLayoutToDesignCoordinates({ ...desiredPosition, z: 0 });
    getLayoutModeCameraControls().target.set(x, y, z);
  }

  getAntiZoomFactor(): number {
    return ((this.top - this.bottom) / this.zoom) * ANTI_ZOOM_FACTOR_PER_VIEWPORT_HEIGHT;
  }

  updateProjectionMatrix() {
    super.updateProjectionMatrix();
    updateAntiZoomFactor(this.getAntiZoomFactor());
  }
}
